import React, { forwardRef } from 'react';
import { Separator } from '@base-ui/react/separator';
import { isEmpty } from 'lodash';

import { reportErrorInProductionOrThrow } from '~/Utils';
import cn from '~/Utils/cn';

export type DividerOrientation = 'horizontal' | 'vertical';

/** `fullWidth` spans the whole container; `middle` indents both ends. */
export type DividerVariant = 'fullWidth' | 'middle';

export interface DividerProps {
  className?: string;
  /** @default 'horizontal' */
  orientation?: DividerOrientation;
  /** @default 'fullWidth' */
  variant?: DividerVariant;
  /**
   * A vertical divider inside a flex row. Stretches to the tallest sibling instead of taking
   * `height: 100%`, which resolves to nothing when the row's height comes from its content.
   */
  flexItem?: boolean;
  /**
   * Native element to render as, where the default `div` is not valid in its parent (an `li`
   * inside a `<ul>`, for instance). Intrinsic elements only: a custom component that did not
   * spread its props would drop the role, orientation and classes Base UI merges into the
   * rendered element.
   */
  as?: keyof React.JSX.IntrinsicElements;
}

const Divider = forwardRef<HTMLElement, DividerProps>(
  ({ className, orientation = 'horizontal', variant = 'fullWidth', flexItem = false, as: As, ...restProps }, ref) => {
    if (!isEmpty(restProps)) {
      reportErrorInProductionOrThrow(
        `Unexpected props passed to Divider: ${Object.keys(restProps).join(', ')}.
Examine if these need to be removed, or added explicitly to the DividerProps interface and to the component's signature.`,
        true
      );
    }

    const isVertical = orientation === 'vertical';

    return (
      <Separator
        // Base UI types the Separator ref as a div, but `as` may render another element.
        ref={ref as React.Ref<HTMLDivElement>}
        orientation={orientation}
        className={cn(
          'm-0 shrink-0 border-0 border-solid border-slate-600',
          isVertical ? 'border-r' : 'border-b',
          isVertical && (flexItem ? 'h-auto self-stretch' : 'h-full'),
          variant === 'middle' && (isVertical ? 'my-8' : 'mx-16'),
          className
        )}
        render={As ? <As /> : undefined}
      />
    );
  }
);

Divider.displayName = 'Divider';

export default Divider;
